import { CalendarToday } from '@mui/icons-material';
import { Grid, IconButton, InputAdornment, Stack } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ActionButton from 'common/components/ActionButton';
import CustomTextValidator from 'common/components/CustomTextValidators';
import CustomTimeRangePicker from 'common/components/CustomTimeRangePicker';
import useFlexBoxStyles from 'common/flexBox';
import { useIncludeDateValidation } from 'common/hooks/useIncludeDateValidation';
import useOnliveStyles from 'common/onliveStyles';
import { useFormFields } from 'libs/hooksLib';
import moment from 'moment-timezone';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidatorForm } from 'react-material-ui-form-validator';

const transformDates = (dates) => {
    return dates.map((dateRange) => ({
        startDate: dateRange.startTime,
        endDate: dateRange.endTime,
    }));
};

const DateOverrideItem = (props) => {
    const { onSubmit } = props;
    const { t } = useTranslation();
    const { classes: flexBox, cx } = useFlexBoxStyles();
    const { classes: onlive } = useOnliveStyles();

    const { isValidHourRange, error, validationType, setError } = useIncludeDateValidation();

    const [fields, handleFieldChange, reset] = useFormFields({
        date: '',
        overrides: [],
    });

    const [open, setOpen] = useState(false);

    const handleEditDate = (newDate, idx) => {
        const newRange = [
            ...fields.overrides.slice(0, idx),
            { startTime: newDate.start, endTime: newDate.end },
            ...fields.overrides.slice(idx + 1),
        ];
        saveRange(newRange);
    };

    const handleAddDate = (newDate) => {
        const newRange = [...fields.overrides, { startTime: newDate.start, endTime: newDate.end }];
        saveRange(newRange);
    };

    const handleDeleteDate = (idx) => {
        deleteError(idx);
        const newRange = [...fields.overrides.slice(0, idx), ...fields.overrides.slice(idx + 1)];
        saveRange(newRange);
    };

    const deleteError = (idx) => {
        if (error?.idx === idx) {
            setError(null);
        }
    };

    const saveRange = (data) => {
        handleFieldChange({
            target: {
                name: 'overrides',
                value: data,
            },
        });
    };

    const handleSubmit = () => {
        reset({
            date: '',
            overrides: [],
        });
        onSubmit({ ...fields });
    };

    const handleChange = (date, idx, callback) => {
        const ranges = transformDates(fields.overrides);
        const isValid = isValidHourRange({ ranges, date, idx, type: validationType.UNSELECTED });
        if (isValid) {
            if (idx === -1) {
                handleAddDate(date);
                callback?.();
            } else {
                handleEditDate(date, idx);
            }
        }
    };

    return (
        <Stack
            direction="column"
            spacing={2}
            className={cx(
                flexBox.overflowHidden,
                flexBox.h100,
                onlive.borderMediumGray,
                flexBox.padding2,
                flexBox.borderRadius2,
            )}
        >
            <Stack direction="column" spacing={3} className={cx(flexBox.overflowHidden, flexBox.h100)}>
                <ValidatorForm
                    onSubmit={() => {}}
                    onError={() => {}}
                    className={cx(flexBox.flex, flexBox.gap2, flexBox.column, flexBox.overflowHidden, flexBox.h100)}
                >
                    <Stack
                        direction="column"
                        className={cx(flexBox.overflowXHidden, flexBox.scrollbarNone, flexBox.h100)}
                    >
                        <Grid container xs={12}>
                            <Grid item xs={4} alignItems="center" className={flexBox.paddingRight1}>
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DesktopDatePicker
                                        value={fields.date ? moment(fields.date, 'YYYY-MM-DD') : null}
                                        onChange={(newValue) =>
                                            handleFieldChange({
                                                target: { name: 'date', value: moment(newValue).format('YYYY-MM-DD') },
                                            })
                                        }
                                        open={open}
                                        onOpen={() => setOpen(true)}
                                        onClose={() => setOpen(false)}
                                        PaperProps={{
                                            style: { borderRadius: '16px' },
                                        }}
                                        renderInput={(params) => (
                                            <CustomTextValidator
                                                {...params}
                                                inputProps={{
                                                    ...params.inputProps,
                                                    readOnly: true,
                                                }}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <InputAdornment position="end" sx={{ mr: 1 }}>
                                                            <IconButton onClick={() => setOpen(true)} size="small">
                                                                <CalendarToday sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                onClick={() => setOpen(true)}
                                            />
                                        )}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={8}>
                                <Stack direction="column" spacing={1}>
                                    {fields.overrides?.map((date, idx) => (
                                        <Stack direction="column" key={idx}>
                                            <CustomTimeRangePicker
                                                date={{ start: date.startTime, end: date.endTime }}
                                                onApply={(date) => handleChange(date, idx)}
                                                onDelete={() => handleDeleteDate(idx)}
                                                error={error?.idx === idx ? error?.msg : null}
                                                disabled={error ? error?.idx !== idx : false}
                                                dates={transformDates(fields.overrides)}
                                            />
                                        </Stack>
                                    ))}
                                    <Stack
                                        direction="column"
                                        style={fields.overrides.length === 0 ? {} : { paddingRight: '36px' }}
                                    >
                                        <CustomTimeRangePicker
                                            onApply={(date) => handleChange(date, -1)}
                                            error={error?.idx === -1 ? error?.msg : null}
                                            disabled={error ? error?.idx !== -1 : !fields.date}
                                            dates={transformDates(fields.overrides)}
                                        />
                                    </Stack>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Stack>
                </ValidatorForm>
            </Stack>

            <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={1}
                className={flexBox.paddingTop2}
            >
                {/* <ActionButton onClick={onClose} color="default" fitWidth>
                    {t('app.common.cancel')}
                </ActionButton> */}

                <ActionButton
                    color="primary"
                    autoFocus
                    fitWidth
                    disabled={fields.overrides.length === 0 || error}
                    onClick={handleSubmit}
                >
                    {t('calendar.common.add')}
                </ActionButton>
            </Stack>
        </Stack>
    );
};

export default DateOverrideItem;
